import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format as formatDate,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";

/** Day index the week starts on: 0 = Sunday … 6 = Saturday. */
export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** An inclusive start/end date selection. Either bound may be unset. */
export interface DateRange {
  readonly start: Date | null;
  readonly end: Date | null;
}

/**
 * Builds the calendar matrix (weeks of seven days) covering `month`, padded
 * with the leading/trailing days needed to fill whole weeks.
 */
export function getMonthMatrix(month: Date, weekStartsOn: WeekStartsOn): Date[][] {
  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
}

/** Short weekday initials starting from `weekStartsOn` (e.g. M T W T F S S). */
export function getWeekdayLabels(weekStartsOn: WeekStartsOn): string[] {
  const base = startOfWeek(new Date(), { weekStartsOn });
  return Array.from({ length: 7 }, (_, i) => formatDate(addDays(base, i), "EEEEE"));
}

/** Whether `date` falls before `minDate` or after `maxDate` (day granularity). */
export function isDateDisabled(date: Date, minDate?: Date, maxDate?: Date): boolean {
  const day = startOfDay(date);
  if (minDate && isBefore(day, startOfDay(minDate))) return true;
  if (maxDate && isAfter(day, startOfDay(maxDate))) return true;
  return false;
}

/**
 * Whether `date` sits strictly between the two bounds (exclusive of the
 * endpoints). The bounds are treated as unordered so a provisional end that
 * precedes the start (e.g. reverse hover during selection) still previews the
 * band — matching the ordered range produced on commit.
 */
export function isInRange(date: Date, start: Date | null, end: Date | null): boolean {
  if (!start || !end) return false;
  const day = startOfDay(date);
  const a = startOfDay(start);
  const b = startOfDay(end);
  const lo = isAfter(a, b) ? b : a;
  const hi = isAfter(a, b) ? a : b;
  return isWithinInterval(day, { start: lo, end: hi }) && !isSameDay(day, lo) && !isSameDay(day, hi);
}

/** Orders two dates into a `[start, end]` range regardless of selection order. */
export function orderRange(a: Date, b: Date): { start: Date; end: Date } {
  return isAfter(a, b) ? { start: b, end: a } : { start: a, end: b };
}

/**
 * Clamps a range to the `[minDate, maxDate]` bounds. Returns `null` when the
 * range lies entirely outside the bounds (so callers can reject it rather than
 * committing dates the calendar would not allow).
 */
export function clampRange(range: DateRange, minDate?: Date, maxDate?: Date): DateRange | null {
  if (!range.start || !range.end) return range;
  let start = startOfDay(range.start);
  let end = startOfDay(range.end);

  if (minDate) {
    const min = startOfDay(minDate);
    if (isBefore(end, min)) return null;
    if (isBefore(start, min)) start = min;
  }
  if (maxDate) {
    const max = startOfDay(maxDate);
    if (isAfter(start, max)) return null;
    if (isAfter(end, max)) end = max;
  }
  if (isAfter(start, end)) return null;
  return { start, end };
}

/** Formats a single date for display in the input field. */
export function formatDisplayDate(date: Date, formatString: string): string {
  return formatDate(date, formatString);
}

/** Formats a range for display in the input field. */
export function formatDisplayRange(
  range: DateRange,
  formatString: string,
  separator = " - ",
  formatter?: (date: Date) => string,
): string {
  const fmt = formatter ?? ((d: Date) => formatDate(d, formatString));
  const start = range.start ? fmt(range.start) : "";
  const end = range.end ? fmt(range.end) : "";
  // Only join with the separator when both bounds are set; a partial range
  // shows just the bound that is present (no dangling/leading separator).
  if (start && end) return `${start}${separator}${end}`;
  return start || end;
}

export { isSameDay, isSameMonth } from "date-fns";

/**
 * Auto-formats raw digit input into "DD/MM/YYYY".
 * Inserts `/` immediately after the day and month digit groups.
 *
 * Uses a slot-based approach: only reformats when digits are appended
 * sequentially at the end. Mid-string edits pass through unchanged to
 * avoid shifting later digits left.
 */
export function maskDateInput(raw: string, previous: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return "";

  if (raw.length < previous.length) return raw;

  const prevDigits = previous.replace(/\D/g, "");

  if (digits.length !== prevDigits.length + 1 || !digits.startsWith(prevDigits)) {
    return raw;
  }

  let result = "";
  for (let i = 0; i < digits.length && i < 8; i++) {
    result += digits.charAt(i);
    if (i === 1 || i === 3) {
      result += "/";
    }
  }
  return result;
}

/**
 * Auto-formats raw digit input into "DD/MM/YYYY - DD/MM/YYYY".
 * Same slot-based approach as {@link maskDateInput} but for two dates
 * separated by ` - `.
 */
export function maskDateRangeInput(raw: string, previous: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 0) return "";

  if (raw.length < previous.length) return raw;

  const prevDigits = previous.replace(/\D/g, "");

  if (digits.length !== prevDigits.length + 1 || !digits.startsWith(prevDigits)) {
    return raw;
  }

  let result = "";
  for (let i = 0; i < digits.length && i < 16; i++) {
    result += digits.charAt(i);
    if (i === 1 || i === 3 || i === 9 || i === 11) {
      result += "/";
    }
    if (i === 7) {
      result += " - ";
    }
  }
  return result;
}
